// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Calendar from "@adl/XDL/Icons/Calendar";
import { xBaseTheme } from "@adl/XDL/types";
import { color } from "@adl/XDL/Utilities";
import DatePickerPresentationPropsDefault from "@insite/mobius/DatePicker/presentationProps";
import { IconWrapper } from "@insite/mobius/Icon";
import { css } from "styled-components";

export const chevronRightString =
    "PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbDpzcGFjZT0icHJlc2VydmUiIHZpZXdCb3g9IjAgMCAxMCAxNiI+PHBhdGggZmlsbD0iI2ZmZiIgZD0iTTkuNyA3LjRjLjQuMy40LjkgMCAxLjJsLTcuMyA3LjFjLS40LjMtLjkuMy0xLjMgMGwtLjktLjhjLS40LS4zLS40LS45IDAtMS4yTDYuMSA4IC4zIDIuM2MtLjQtLjMtLjQtLjkgMC0xLjJsLjktLjhjLjQtLjMuOS0uMyAxLjMgMGw3LjIgNy4xeiIvPjwvc3ZnPg==";

export const chevronLeftString =
    "PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbDpzcGFjZT0icHJlc2VydmUiIHZpZXdCb3g9IjAgMCAxMCAxNiI+PHBhdGggZmlsbD0iI2ZmZiIgZD0iTTcuNi4zQzggMCA4LjUgMCA4LjkuM2wuOS44Yy40LjMuNC45IDAgMS4yTDMuOSA4bDUuOCA1LjdjLjQuMy40LjkgMCAxLjJsLS45LjhjLS40LjMtLjkuMy0xLjMgMEwuMyA4LjZjLS40LS4zLS40LS45IDAtMS4yTDcuNi4zeiIvPjwvc3ZnPg==";

const arrowIcons = css`
    &__next-button {
        background-image: url("${`data:image/svg+xml;base64,${chevronRightString}`}");
    }
    &__prev-button {
        background-image: url("${`data:image/svg+xml;base64,${chevronLeftString}`}");
    }
`;

export default {
    defaultProps: {
        ...DatePickerPresentationPropsDefault,
        calendarIconProps: {
            ...DatePickerPresentationPropsDefault?.calendarIconProps,
            src: Calendar,
            color: "common.ui6"
        },
        dateTimePickerProps: {
            showNeighboringMonth: true,
        },
        cssOverrides: {
            datePicker: css`
                button:focus {
                    /* outline is added on span for border reasons */
                    outline: none;
                    span {
                        outline: none;
                    }
                }
                .react-datetime-picker {
                    &__clear-button  {
                        display: none;
                    }
                    &__calendar-button:hover {
                        ${IconWrapper} {
                            color: ${color("common.ui1")}
                        }
                    }
                }
                
                .react-calendar {
                    width: 250px; // to fix small mobile screen size issue
                    border-radius: 2px 2px 0 0;
                    padding: 0;
                    button {
                        &:enabled {
                            &:hover:not(:disabled),
                            &:focus:not(:disabled) {
                                background-color: ${color("common.ui11")};
                                cursor: pointer;
                            }
                        }
                    }
                    
                    &__navigation {
                        padding: 8px;
                        border-radius: 2px 2px 0 0;
                        margin-bottom: 0;
                        align-items: center;
                        background-color: ${color("primary")};

                        button {
                            background-color: transparent;
                            border-radius: 4px;
                            min-width: 6px;
                            &:enabled {
                                &:hover:not(:disabled),
                                &:focus:not(:disabled) {
                                    color: ${color("common.white")};
                                    background-color: ${color("primary")};
                                    cursor: pointer;
                                }
                            }
                        }
                        &__label {
                            font-size: 14px;
                            font-weight: normal;
                            font-family: inherit;
                            letter-spacing: 0.5px;
                            line-height: 19px;
                            color: ${color("common.white")};
                            background-color: transparent;
                            margin: 0 20px;
                        }
                        &__arrow {
                            width: 6px;
                            height: 10px;
                            margin: 0;
                        }
                        ${arrowIcons}
                    }

                    &__month-view {
                        &__weekdays {
                            background-color: ${color("primary")};
                            color: ${color("common.white")};
                            font-size: 12px;
                            font-weight: 200;
                            letter-spacing: 0;
                            line-height: 17px;
                            padding-bottom: 4px;
                        }

                        &__days {
                            border: 1px solid #ced1d2;
                            background-color: ${color("common.white")};

                            &__day {
                                border-radius: 0;
                                cursor: pointer;
                                font-size: 14px;
                                letter-spacing: 0;
                                line-height: 32px;
                                padding: 0 8px;
                                margin: 0;
                                border: 1px solid ${color("common.ui4")};
                            }
                        }
                    }
                }
            `,
        },
    },
} as xBaseTheme["datePicker"];
